import { useState, type ReactNode } from 'react';
import { TEXT_SCALES, setPreferences, usePreferences, type TextScale, type ThemePreference } from '../../lib/preferences';
import { Dialog, DialogClose, RadioGroup, Switch } from '../../components/ui';

/** Text size, colour theme and high contrast (R-5.7). Applies immediately and is remembered on this device. */
export function DisplaySettingsForm() {
  const prefs = usePreferences();
  return (
    <div className="flex flex-col gap-6">
      <RadioGroup<string>
        name="text-size"
        legend="Text size"
        hint="Makes all text and buttons bigger on this device."
        inline
        options={TEXT_SCALES.map((s) => ({ value: String(s), label: `${Math.round(s * 100)}%` }))}
        value={String(prefs.textScale)}
        onChange={(v) => setPreferences({ textScale: Number(v) as TextScale })}
      />
      <RadioGroup<ThemePreference>
        name="theme"
        legend="Colours"
        inline
        options={[
          { value: 'system', label: 'Match my device' },
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
        ]}
        value={prefs.theme}
        onChange={(v) => setPreferences({ theme: v })}
      />
      <Switch
        label="High contrast"
        hint="Stronger borders and text colours."
        checked={prefs.highContrast}
        onCheckedChange={(v) => setPreferences({ highContrast: v })}
      />
    </div>
  );
}

/** Dialog wrapper. Pass a trigger button, or control `open` yourself. */
export function DisplaySettingsDialog({
  trigger,
  open,
  onOpenChange,
}: {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internal, setInternal] = useState(false);
  return (
    <Dialog
      open={open ?? internal}
      onOpenChange={onOpenChange ?? setInternal}
      trigger={trigger}
      title="Display settings"
      description="These settings only affect this device."
      footer={<DialogClose>Done</DialogClose>}
    >
      <DisplaySettingsForm />
    </Dialog>
  );
}
